import { ApiError } from '../identity/types';

/**
 * EPN-71 Phase 1 — failure classification for the Pay Employees screen.
 *
 * The screen NEVER renders `err.message`: an ApiError's message can carry a server `detail` string or the
 * internal request path ("Request to /api/... failed with 500"), neither of which belongs in front of a
 * user. Every failure is reduced to one of these reasons, and the copy for each reason is fixed here.
 */
export type FailureReason = 'forbidden' | 'session' | 'unavailable' | 'generic';

export function classifyFailure(err: unknown): FailureReason {
  if (err instanceof ApiError) {
    if (err.status === 403) return 'forbidden';
    if (err.status === 401) return 'session';
    // A downstream module (Employees, Compensation) could not be reached by the module we called.
    if (err.status === 502 || err.status === 503 || err.status === 504) return 'unavailable';
    return 'generic';
  }
  // fetch() rejects with a TypeError when the network or the service is unreachable.
  if (err instanceof TypeError) return 'unavailable';
  return 'generic';
}

export function employeesFailureCopy(reason: FailureReason): string {
  switch (reason) {
    case 'forbidden':
      return 'You don’t have permission to view employees. Ask an administrator to grant you access.';
    case 'session':
      return 'Your session may have expired. Sign in again, then retry.';
    case 'unavailable':
      return 'Employees can’t be loaded right now because a connected service isn’t responding. Try again shortly.';
    default:
      return 'Employees couldn’t be loaded. Try again.';
  }
}

export function readinessFailureCopy(reason: FailureReason): string {
  switch (reason) {
    case 'forbidden':
      return 'You don’t have permission to view payroll readiness, so payroll can’t continue from this screen. Ask an administrator to grant you access.';
    case 'session':
      return 'Your session may have expired. Sign in again, then retry the readiness check.';
    case 'unavailable':
      return 'Payroll readiness can’t be checked right now because a connected service isn’t responding. Payroll can’t continue until it is confirmed. Try again shortly.';
    default:
      return 'Payroll readiness couldn’t be checked. Payroll can’t continue until it is confirmed. Try again.';
  }
}
